export const DEFAULT_THEME = {
  mode: "light", primary: "#D97706", secondary: "#1D4ED8", bg: "#F1F5F9", card: "#FFFFFF", text: "#0F172A",
  sidebar: "#0A0E17", button_bg: "", button_text: "#FFFFFF", radius: 10, font: "IBM Plex Sans",
  card_style: "shadow", button_style: "rounded", nav_style: "dark", icon_style: "regular",
};

const DARK = { bg: "#0B1220", card: "#111A2E", text: "#E5E7EB", sidebar: "#060A14" };
const FONTS = { "IBM Plex Sans": "'IBM Plex Sans', sans-serif", Outfit: "'Outfit', sans-serif", Manrope: "'Manrope', sans-serif", "Source Sans 3": "'Source Sans 3', sans-serif", "Roboto Slab": "'Roboto Slab', serif" };

function hexToHsl(hex) {
  let r = 0, g = 0, b = 0;
  try { r = parseInt(hex.slice(1, 3), 16) / 255; g = parseInt(hex.slice(3, 5), 16) / 255; b = parseInt(hex.slice(5, 7), 16) / 255; } catch { return "25 95% 43%"; }
  const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) { const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4; h /= 6; }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
const darken = (hex, f = 0.85) => { try { const n = parseInt(hex.slice(1), 16); const r = Math.round(((n >> 16) & 255) * f), g = Math.round(((n >> 8) & 255) * f), b = Math.round((n & 255) * f); return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`; } catch { return hex; } };

export function resolveTheme(theme) {
  const t = { ...DEFAULT_THEME, ...(theme || {}) };
  const sysDark = typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  const dark = t.mode === "dark" || (t.mode === "system" && sysDark);
  if (dark) {
    if (!theme?.bg || theme.bg === DEFAULT_THEME.bg) t.bg = DARK.bg;
    if (!theme?.card || theme.card === DEFAULT_THEME.card) t.card = DARK.card;
    if (!theme?.text || theme.text === DEFAULT_THEME.text) t.text = DARK.text;
  }
  return { ...t, dark };
}

export function themeVars(theme) {
  const t = resolveTheme(theme);
  const btn = t.button_bg || t.primary;
  const radius = t.button_style === "pill" ? "999px" : t.button_style === "sharp" ? "0px" : `${t.radius}px`;
  return {
    "--amber": t.primary, "--amber-2": darken(t.primary), "--blue": t.secondary,
    "--bg": t.bg, "--bg-2": t.dark ? "#1E293B" : "#E2E8F0", "--card": t.card, "--text": t.text,
    "--text-2": t.dark ? "#CBD5E1" : "#475569", "--text-3": t.dark ? "#94A3B8" : "#64748B",
    "--border": t.dark ? "#1F2A44" : "#E2E8F0", "--border-c": t.dark ? "222 32% 20%" : "214 32% 88%",
    "--sidebar": t.nav_style === "light" ? "#FFFFFF" : t.nav_style === "colored" ? t.primary : t.sidebar,
    "--sidebar-2": t.dark ? "#0F172A" : "#111827",
    "--primary": hexToHsl(t.primary), "--ring": hexToHsl(t.primary), "--primary-foreground": hexToHsl(t.button_text || "#FFFFFF"),
    "--btn-bg": btn, "--btn-text": t.button_text || "#FFFFFF", "--btn-radius": radius,
    "--radius": `${t.radius}px`, "--font-body": FONTS[t.font] || FONTS["IBM Plex Sans"],
    "--card-shadow": t.card_style === "flat" ? "none" : t.card_style === "outline" ? "none" : "0 1px 2px rgba(15,23,42,0.06)",
    "--card-border": t.card_style === "flat" ? "transparent" : (t.dark ? "#1F2A44" : "#E2E8F0"),
    "--icon-stroke": t.icon_style === "bold" ? "2.5" : t.icon_style === "thin" ? "1.5" : "2",
    "--background": hexToHsl(t.bg), "--foreground": hexToHsl(t.text), "--card-bg": hexToHsl(t.card), "--popover": hexToHsl(t.card), "--popover-foreground": hexToHsl(t.text),
  };
}

export function applyTheme(theme, root = document.documentElement) {
  const vars = themeVars(theme);
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  root.dataset.theme = resolveTheme(theme).dark ? "dark" : "light";
  root.dataset.navStyle = (theme || {}).nav_style || "dark";
}

export const BRAND_KEY = "piche_branding";
export const BRAND_PX = { small: { logo: 32, mark: 22 }, medium: { logo: 40, mark: 30 }, large: { logo: 48, mark: 38 } };
export const brandPx = (size) => BRAND_PX[size] || BRAND_PX.medium;
export function clearBranding() {
  localStorage.removeItem(BRAND_KEY);
  applyTheme(null);
  document.title = "KONVIA";
  const link = document.getElementById("favicon-link");
  if (link) link.href = "/favicon.ico";
}
export function cacheBranding(company) {
  if (!company) return;
  localStorage.setItem(BRAND_KEY, JSON.stringify({ id: company.id, name: company.name, app_name: company.app_name, logo_file_id: company.logo_file_id, logo_url: company.logo_url, favicon_file_id: company.favicon_file_id, theme: company.theme }));
}
export function getCachedBranding() { try { return JSON.parse(localStorage.getItem(BRAND_KEY) || "null"); } catch { return null; } }
